import * as Sentry from '@sentry/react'
import { useState } from 'react'
import { sessionStore } from '../sharedStores/sessionStore'

/**
 * Test page for Sentry error tracking
 * Only accessible in non-production environments
 */
export function TestSentryPage() {
	const [shouldThrowInRender, setShouldThrowInRender] = useState(false)
	const user = sessionStore((state) => state.user)

	// Redirect to dashboard in production
	if (import.meta.env.VITE_ENVIRONMENT === 'prod') {
		window.location.href = '/dashboard'
		return null
	}

	// Test error boundary by throwing in render
	if (shouldThrowInRender) {
		throw new Error('Test error thrown during render (Error Boundary test)')
	}

	const testUnhandledError = () => {
		throw new Error('Test unhandled error - should be caught by Sentry')
	}

	const testAsyncError = () => {
		setTimeout(() => {
			throw new Error('Test async error - should be caught by Sentry')
		}, 100)
	}

	const testHandledError = () => {
		try {
			throw new Error('Test handled error')
		} catch (error) {
			Sentry.captureException(error)
			alert('Handled error captured and sent to Sentry')
		}
	}

	const testCustomMessage = () => {
		Sentry.captureMessage('Test custom message from admin app', 'info')
		alert('Custom message sent to Sentry')
	}

	const testWithContext = () => {
		Sentry.captureException(new Error('Test error with custom context'), {
			tags: {
				test_type: 'manual',
				feature: 'sentry_integration',
			},
			extra: {
				custom_data: 'This is additional context',
				timestamp: new Date().toISOString(),
			},
		})
		alert('Error with custom context sent to Sentry')
	}

	return (
		<div className='mx-auto max-w-4xl p-8'>
			<div className='mb-6 rounded-lg bg-yellow-50 p-4 border border-yellow-200'>
				<h1 className='text-2xl font-bold text-yellow-900 mb-2'>Sentry Error Testing</h1>
				<p className='text-yellow-800'>
					Environment: <strong>{import.meta.env.VITE_ENVIRONMENT}</strong>
				</p>
				<p className='text-yellow-800'>This page is only available in non-production environments.</p>
			</div>

			{user && (
				<div className='mb-6 rounded-lg bg-blue-50 p-4 border border-blue-200'>
					<h2 className='text-lg font-semibold text-blue-900 mb-2'>User Context</h2>
					<p className='text-blue-800'>
						<strong>Name:</strong> {user.firstName} {user.lastName}
					</p>
					<p className='text-blue-800'>
						<strong>Email:</strong> {user.email}
					</p>
					<p className='text-blue-800'>
						<strong>Organization:</strong> {user.organization?.name || 'N/A'}
					</p>
					<p className='text-sm text-blue-600 mt-2'>This context will be included with all errors sent to Sentry.</p>
				</div>
			)}

			<div className='space-y-4'>
				<h2 className='text-xl font-semibold mb-4'>Test Error Scenarios</h2>

				<div className='p-4 border rounded-lg'>
					<h3 className='font-semibold mb-2'>1. Unhandled Error</h3>
					<p className='text-sm text-gray-600 mb-2'>
						Throws an error immediately. Should be caught by Sentry's global error handler.
					</p>
					<button
						type='button'
						onClick={testUnhandledError}
						className='px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600'>
						Throw Unhandled Error
					</button>
				</div>

				<div className='p-4 border rounded-lg'>
					<h3 className='font-semibold mb-2'>2. Async Error</h3>
					<p className='text-sm text-gray-600 mb-2'>Throws an error in a setTimeout. Tests async error handling.</p>
					<button
						type='button'
						onClick={testAsyncError}
						className='px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600'>
						Throw Async Error
					</button>
				</div>

				<div className='p-4 border rounded-lg'>
					<h3 className='font-semibold mb-2'>3. Error Boundary Test</h3>
					<p className='text-sm text-gray-600 mb-2'>
						Throws an error during render. Should be caught by React Error Boundary.
					</p>
					<button
						type='button'
						onClick={() => setShouldThrowInRender(true)}
						className='px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600'>
						Throw Render Error
					</button>
				</div>

				<div className='p-4 border rounded-lg'>
					<h3 className='font-semibold mb-2'>4. Handled Error</h3>
					<p className='text-sm text-gray-600 mb-2'>
						Manually captures and sends an error to Sentry using captureException.
					</p>
					<button
						type='button'
						onClick={testHandledError}
						className='px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600'>
						Send Handled Error
					</button>
				</div>

				<div className='p-4 border rounded-lg'>
					<h3 className='font-semibold mb-2'>5. Custom Message</h3>
					<p className='text-sm text-gray-600 mb-2'>
						Sends a custom message to Sentry (not an error, just a log message).
					</p>
					<button
						type='button'
						onClick={testCustomMessage}
						className='px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600'>
						Send Custom Message
					</button>
				</div>

				<div className='p-4 border rounded-lg'>
					<h3 className='font-semibold mb-2'>6. Error with Custom Context</h3>
					<p className='text-sm text-gray-600 mb-2'>Sends an error with additional tags and context data.</p>
					<button
						type='button'
						onClick={testWithContext}
						className='px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-600'>
						Send Error with Context
					</button>
				</div>
			</div>

			<div className='mt-8 p-4 bg-gray-50 rounded-lg border border-gray-200'>
				<h3 className='font-semibold mb-2'>What to Check in Sentry</h3>
				<ul className='list-disc list-inside space-y-1 text-sm text-gray-700'>
					<li>Error appears in the correct project (admin)</li>
					<li>Release is tagged as admin@{'{commit-sha}'}</li>
					<li>User context shows your name, email, and organization</li>
					<li>Stack traces show TypeScript code (not minified)</li>
					<li>Environment is set to: {import.meta.env.VITE_ENVIRONMENT}</li>
					<li>Custom tags and context appear on error #6</li>
				</ul>
			</div>
		</div>
	)
}
